/**
 * "A new version of the console is ready." (2026-10-31)
 *
 * A deploy never reached a tab that was already open. `public/sw.js`
 * has fixed cache names and no build stamp, so its bytes do not change
 * between deploys: the browser never installs a new worker, and the
 * `nexsoss:sw-update-ready` event `sw-register.ts` fires for one never
 * fires. The front desk's iPads keep the console open for days, so they
 * ran the old build until somebody happened to reload.
 *
 * So the tab asks for itself. Every five minutes, and when the tab is
 * shown again, it reads `index.html` past every cache and compares the
 * entry script that page names -- `/assets/index-<hash>.js`, a new hash
 * for every build -- with the one this page loaded. When they differ,
 * the shell shows a small banner with a Reload button
 * (`NewBuildBanner`).
 *
 * NEVER RELOADS BY ITSELF. An operator may be half way through a form
 * or a schedule edit; the banner waits for them. The service worker's
 * own signals (`nexsoss:sw-update-ready`, and a new worker taking over
 * an open tab, which `main.tsx` passes on as the same event) check at
 * once and raise the same banner instead of reloading.
 *
 * Quiet on failure: a read that fails, answers something other than the
 * page, or names no entry script says nothing, and the next check tries
 * again. Production builds only (`startNewBuildWatch`): a dev server's
 * page has no hashed entry and reloads itself anyway.
 */

/** How often an open tab asks whether a new build is out. */
export const NEW_BUILD_CHECK_MS = 5 * 60_000;
/** Showing the tab again checks at once, but not more often than this. */
export const NEW_BUILD_MIN_GAP_MS = 30_000;
/** The page the server would hand a reload. */
export const INDEX_URL = '/index.html';
/** Fired by `sw-register.ts` when a new service worker has installed. */
export const SW_UPDATE_READY_EVENT = 'nexsoss:sw-update-ready';

const HASHED_ENTRY = /\/assets\/index-[^/]+\.js$/;

/**
 * The entry module a page loads, as a path: the hashed
 * `/assets/index-*.js` when the page names one, else its first module
 * script. `html` is a fetched page's text, or a live document. Null when
 * the page names no module script at all (an error page, the service
 * worker's offline page).
 */
export function entryModulePath(html: string | Document, baseUrl?: string): string | null {
  const doc =
    typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
  const base = baseUrl ?? (typeof window !== 'undefined' ? window.location.href : 'http://x/');
  const paths: string[] = [];
  for (const el of doc.querySelectorAll('script[type="module"][src]')) {
    const src = el.getAttribute('src');
    if (!src) continue;
    try {
      paths.push(new URL(src, base).pathname);
    } catch {
      // A src that is not a URL names nothing to compare.
    }
  }
  return paths.find((p) => HASHED_ENTRY.test(p)) ?? paths[0] ?? null;
}

/** Whether the page the server hands out now loads another build than
 *  this one. Unknown on either side is never "new", and neither is a
 *  page with no hashed entry when this one loaded a hashed one: that is
 *  someone else's page answering /index.html (a sign-in or challenge
 *  page), not a build of ours. */
export function isNewBuild(loaded: string | null, served: string | null): boolean {
  if (loaded === null || served === null || loaded === served) return false;
  return !HASHED_ENTRY.test(loaded) || HASHED_ENTRY.test(served);
}

/** The entry module of the page the server hands out now, read past the
 *  browser's cache. Null when it could not be read. */
export async function fetchServedEntry(
  fetchImpl: typeof fetch = fetch,
  url: string = INDEX_URL,
): Promise<string | null> {
  try {
    const res = await fetchImpl(url, { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return null;
    return entryModulePath(await res.text());
  } catch {
    return null;
  }
}

// -- whether a new build is ready: one flag for the whole page -------------

let ready = false;
const listeners = new Set<() => void>();

export function isNewBuildReady(): boolean {
  return ready;
}

/** Say a new build is out. Once is enough: the banner stays until the
 *  page is reloaded. */
export function markNewBuildReady(): void {
  if (ready) return;
  ready = true;
  for (const l of [...listeners]) l();
}

export function subscribeNewBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface NewBuildWatchOptions {
  /** The entry this page loaded; read from `document` when absent. */
  loadedEntry?: string | null;
  fetchImpl?: typeof fetch;
  intervalMs?: number;
  now?: () => number;
}

/**
 * Check on a timer and whenever the tab is shown again, and at once on
 * the service worker's signal. Every way in ends at `markNewBuildReady`.
 * Returns a stop function. Not gated on the build: `startNewBuildWatch`
 * is.
 */
export function watchForNewBuild(opts: NewBuildWatchOptions = {}): () => void {
  const fetchImpl = opts.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const now = opts.now ?? Date.now;
  const loaded = opts.loadedEntry !== undefined ? opts.loadedEntry : entryModulePath(document);
  let stopped = false;
  let inFlight = false;
  let lastCheck = now();

  /** `signal`: the service worker says a new one is out. The page is
   *  still compared -- a worker can change with the build it serves
   *  unchanged, and the page the tab has may already be the new one --
   *  but when that cannot be told, the worker is taken at its word. */
  const check = async (signal = false) => {
    if (stopped || ready) return;
    if (!signal && (inFlight || loaded === null)) return;
    inFlight = true;
    lastCheck = now();
    try {
      const served = loaded === null ? null : await fetchServedEntry(fetchImpl);
      if (stopped) return;
      if (isNewBuild(loaded, served)) markNewBuildReady();
      else if (signal && served === null) markNewBuildReady();
    } finally {
      inFlight = false;
    }
  };
  const onWorker = () => void check(true);

  // A hidden tab does not ask: showing it again does, at once.
  const timer = window.setInterval(() => {
    if (document.visibilityState !== 'hidden') void check();
  }, opts.intervalMs ?? NEW_BUILD_CHECK_MS);
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    if (now() - lastCheck < NEW_BUILD_MIN_GAP_MS) return;
    void check();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener(SW_UPDATE_READY_EVENT, onWorker);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener(SW_UPDATE_READY_EVENT, onWorker);
  };
}

let stopWatch: (() => void) | null = null;

/** Start watching for a new build, once, in a production build only. */
export function startNewBuildWatch(): void {
  if (stopWatch !== null || typeof window === 'undefined') return;
  if (!import.meta.env.PROD) return;
  stopWatch = watchForNewBuild();
}

/** Test helper: forget the flag and its listeners, stop any watch. */
export function __resetNewBuildForTests(): void {
  ready = false;
  listeners.clear();
  stopWatch?.();
  stopWatch = null;
}
